import React, { useState, useEffect } from 'react';
import { Mail, Send, Copy, Check, AlertCircle, Loader2, RefreshCw, CreditCard, CheckCircle, XCircle, BarChart3 } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminResponseTab = ({ 
  password,
  fetchStats,
  formatDate,
  getPriorityColor,
  initialEmail = '',
  initialSubject = '',
  initialMessage = '',
  initialTimestamp = null
}) => {
  // Form state
  const [customerEmail, setCustomerEmail] = useState(initialEmail);
  const [subject, setSubject] = useState(initialSubject);
  const [message, setMessage] = useState(initialMessage);
  const [response, setResponse] = useState(null);
  const [editedResponse, setEditedResponse] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  
  // Fresh link state
  const [paymentVerification, setPaymentVerification] = useState(null);
  const [isVerifyingPayment, setIsVerifyingPayment] = useState(false);
  const [isSendingFreshLink, setIsSendingFreshLink] = useState(false);

  // Sync props with state when clicking on a message from Messages tab
  useEffect(() => {
    if (initialTimestamp) {
      setCustomerEmail(initialEmail);
      setSubject(initialSubject);
      setMessage(initialMessage);
      setResponse(null);
      setEditedResponse('');
      setPaymentVerification(null);
    }
  }, [initialTimestamp, initialEmail, initialSubject, initialMessage]);

  const handleGenerateResponse = async (e) => {
    e.preventDefault();
    if (!customerEmail || !message) {
      toast.error('Please fill in customer email and message');
      return;
    }

    setIsLoading(true);
    setResponse(null);

    try {
      const res = await fetch(`${API_URL}/api/admin/generate-response`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          password,
          customer_email: customerEmail,
          subject: subject || 'Support Request',
          message
        })
      });

      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to generate response');
      }

      setResponse(data);
      toast.success('Response generated!');
      fetchStats();
    } catch (error) {
      toast.error(error.message || 'Something went wrong');
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = () => {
    const textToCopy = isEditing ? editedResponse : response?.suggested_response;
    if (textToCopy) {
      navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      toast.success('Copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const saveAsLearning = async () => {
    const responseText = editedResponse || response?.suggested_response;
    if (!responseText) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/add-learning`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          password,
          category: response?.category || 'general',
          question_summary: subject || message.substring(0, 100),
          solution: responseText
        })
      });

      if (!res.ok) {
        throw new Error('Failed to save learning');
      }

      toast.success('Learning saved! Tezza will remember this for future questions.');
    } catch (error) {
      toast.error(error.message);
    }
  };

  const sendEmail = async () => {
    const responseText = editedResponse || response?.suggested_response;
    if (!responseText || !customerEmail) {
      toast.error('Missing email or response');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/admin/send-response`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          password,
          recipient_email: customerEmail,
          subject: subject || 'Your message to Washed Away',
          response_body: responseText
        })
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Failed to send email. Domain may need verification on Resend.');
      }

      toast.success('Email sent to ' + customerEmail);
      clearForm();
      fetchStats();
    } catch (error) {
      toast.error(error.message || 'Failed to send email');
    }
  };

  const clearForm = () => {
    setCustomerEmail('');
    setSubject('');
    setMessage('');
    setResponse(null);
    setEditedResponse('');
    setIsEditing(false);
    setPaymentVerification(null);
  };

  const startEditing = () => {
    setEditedResponse(response?.suggested_response || '');
    setIsEditing(true);
  };

  const verifyPayment = async () => {
    if (!customerEmail) {
      toast.error('Please enter the customer email first');
      return;
    }
    
    setIsVerifyingPayment(true);
    setPaymentVerification(null);
    
    try {
      const res = await fetch(`${API_URL}/api/admin/verify-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          password,
          email: customerEmail
        })
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to verify payment');
      }
      
      setPaymentVerification(data);
      
      if (data.has_payment) {
        toast.success('Payment verified!');
      } else {
        toast.warning('No payment found for this email');
      }
    } catch (error) {
      toast.error(error.message);
      setPaymentVerification({ has_payment: false, message: error.message });
    } finally {
      setIsVerifyingPayment(false);
    }
  };

  const sendFreshLink = async () => {
    if (!customerEmail) {
      toast.error('Please enter the customer email');
      return;
    }
    
    if (!paymentVerification?.has_payment) {
      toast.error('Please verify payment first');
      return;
    }
    
    setIsSendingFreshLink(true);
    
    try {
      const res = await fetch(`${API_URL}/api/admin/send-fresh-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          password,
          email: customerEmail
        })
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to send fresh link');
      }
      
      toast.success(`Fresh magic link sent to ${customerEmail}!`);
      setPaymentVerification(null);
      fetchStats();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsSendingFreshLink(false);
    }
  };

  return (
    <div className="admin-grid" data-testid="response-tab">
      <div className="admin-card">
        <h2><Mail className="h-5 w-5" /> Customer Email</h2>
        <form onSubmit={handleGenerateResponse}>
          <div className="admin-field">
            <label>Customer Email Address</label>
            <input
              type="email"
              value={customerEmail}
              onChange={(e) => {
                setCustomerEmail(e.target.value);
                setPaymentVerification(null);
              }}
              placeholder="customer@example.com"
              className="admin-input"
              data-testid="customer-email-input"
            />
          </div>
          
          {/* Fresh Link Section */}
          <div className="fresh-link-section" style={{
            background: '#f8fafc',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '16px',
            border: '1px solid #e2e8f0'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <RefreshCw className="h-4 w-4" style={{ color: '#22c55e' }} />
              <span style={{ fontWeight: '600', fontSize: '14px', color: '#1a1a2e' }}>
                Streaming Issue? Send Fresh Link
              </span>
            </div>
            
            <p style={{ fontSize: '13px', color: '#666', marginBottom: '12px' }}>
              If the customer paid but is having trouble streaming, verify their payment and send a fresh magic link.
            </p>
            
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={verifyPayment}
                disabled={!customerEmail || isVerifyingPayment}
                style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                data-testid="verify-payment-btn"
              >
                {isVerifyingPayment ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <CreditCard className="h-4 w-4" />
                )}
                Verify Payment
              </Button>
              
              <Button
                type="button"
                size="sm"
                onClick={sendFreshLink}
                disabled={!paymentVerification?.has_payment || isSendingFreshLink}
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '6px',
                  background: paymentVerification?.has_payment ? '#22c55e' : '#d1d5db',
                  color: paymentVerification?.has_payment ? 'white' : '#9ca3af'
                }}
                data-testid="send-fresh-link-btn"
              >
                {isSendingFreshLink ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                Send Fresh Link
              </Button>
            </div>
            
            {/* Payment Verification Result */}
            {paymentVerification && (
              <div style={{
                marginTop: '12px',
                padding: '12px',
                borderRadius: '6px',
                background: paymentVerification.has_payment ? '#f0fdf4' : '#fef2f2',
                border: `1px solid ${paymentVerification.has_payment ? '#bbf7d0' : '#fecaca'}`
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                  {paymentVerification.has_payment ? (
                    <CheckCircle className="h-4 w-4" style={{ color: '#22c55e' }} />
                  ) : (
                    <XCircle className="h-4 w-4" style={{ color: '#ef4444' }} />
                  )}
                  <span style={{ 
                    fontWeight: '600', 
                    fontSize: '13px',
                    color: paymentVerification.has_payment ? '#166534' : '#991b1b'
                  }}>
                    {paymentVerification.has_payment ? 'Payment Verified' : 'No Payment Found'}
                  </span>
                </div>
                
                {paymentVerification.has_payment ? (
                  <div style={{ fontSize: '12px', color: '#555', marginTop: '8px' }}>
                    <div>Amount: ${paymentVerification.amount} {paymentVerification.currency?.toUpperCase()}</div>
                    {paymentVerification.paid_at && (
                      <div>Paid: {formatDate(paymentVerification.paid_at)}</div>
                    )}
                    {paymentVerification.access_info && (
                      <div style={{ marginTop: '4px', color: paymentVerification.access_info.is_expired ? '#dc2626' : '#16a34a' }}>
                        Access: {paymentVerification.access_info.is_expired ? 'Expired' : 'Active'}
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '8px', lineHeight: '1.5' }}>
                    <p style={{ margin: '0 0 10px 0' }}>This email address is not in our payment records.</p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setResponse({
                          suggested_response: `Hi there,\n\nThank you for reaching out about accessing Washed Away.\n\nWe've checked our records and unfortunately we couldn't find a payment associated with this email address (${customerEmail}).\n\nTo watch the documentary, you'll need to purchase stream access at washedaway.au. Once purchased, you'll receive a magic link via email that gives you 48 hours of viewing access.\n\nIf you believe you've already paid using a different email address, please let us know and we'll check that for you.\n\nWarm regards,\nThe Washed Away Team`,
                          category: 'PAYMENT',
                          priority: 'MEDIUM'
                        });
                        setEditedResponse(`Hi there,\n\nThank you for reaching out about accessing Washed Away.\n\nWe've checked our records and unfortunately we couldn't find a payment associated with this email address (${customerEmail}).\n\nTo watch the documentary, you'll need to purchase stream access at washedaway.au. Once purchased, you'll receive a magic link via email that gives you 48 hours of viewing access.\n\nIf you believe you've already paid using a different email address, please let us know and we'll check that for you.\n\nWarm regards,\nThe Washed Away Team`);
                      }}
                      style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                    >
                      <Mail className="h-3 w-3" />
                      Use "No Payment" Template
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
          
          <div className="admin-field">
            <label>Subject</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Email subject line"
              className="admin-input"
              data-testid="subject-input"
            />
          </div>
          <div className="admin-field">
            <label>Email Content</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Paste the customer's email here..."
              className="admin-textarea"
              rows={8}
              data-testid="message-input"
            />
          </div>
          <div className="admin-actions">
            <Button 
              type="button" 
              variant="outline" 
              onClick={clearForm}
              className="admin-clear-btn"
            >
              Clear
            </Button>
            <Button 
              type="submit" 
              disabled={isLoading}
              className="admin-generate-btn"
              data-testid="generate-btn"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4" />
                  Generate Response
                </>
              )}
            </Button>
          </div>
        </form>
      </div>

      <div className="admin-card">
        <h2><AlertCircle className="h-5 w-5" /> AI Suggested Response</h2>
        
        {response ? (
          <>
            <div className="admin-meta">
              <span 
                className="admin-badge"
                style={{ background: getPriorityColor(response.priority) }}
              >
                {response.priority} priority
              </span>
              <span className="admin-badge category">
                {response.category.replace('_', ' ')}
              </span>
            </div>
            
            <div className="admin-response">
              {isEditing ? (
                <textarea
                  value={editedResponse}
                  onChange={(e) => setEditedResponse(e.target.value)}
                  className="admin-response-textarea"
                  placeholder="Write your custom response here..."
                  data-testid="edit-response-textarea"
                />
              ) : (
                <pre>{response.suggested_response}</pre>
              )}
            </div>
            
            <div className="response-actions">
              {!isEditing ? (
                <>
                  <Button 
                    onClick={startEditing}
                    variant="outline"
                    className="admin-edit-btn"
                  >
                    <Mail className="h-4 w-4" />
                    Edit Response
                  </Button>
                  <Button 
                    onClick={() => { setResponse(null); setEditedResponse(''); setIsEditing(false); }}
                    variant="outline"
                    className="admin-clear-btn"
                  >
                    <AlertCircle className="h-4 w-4" />
                    Discard
                  </Button>
                </>
              ) : (
                <Button 
                  onClick={() => setIsEditing(false)}
                  variant="outline"
                  className="admin-edit-btn"
                >
                  <Check className="h-4 w-4" />
                  Done Editing
                </Button>
              )}
            </div>
            
            <div className="response-actions" style={{marginTop: '0.5rem'}}>
              <Button 
                onClick={copyToClipboard}
                className="admin-copy-btn"
              >
                {copied ? (
                  <>
                    <Check className="h-4 w-4" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4" />
                    Copy Response
                  </>
                )}
              </Button>
              <Button 
                onClick={saveAsLearning}
                variant="outline"
                className="admin-learn-btn"
              >
                <BarChart3 className="h-4 w-4" />
                Save as Learning
              </Button>
            </div>
            
            <Button 
              onClick={sendEmail}
              className="admin-send-btn"
              size="lg"
              data-testid="send-email-btn"
            >
              <Send className="h-5 w-5" />
              Send Email to {customerEmail}
            </Button>
          </>
        ) : (
          <div className="admin-empty">
            <p>Paste a customer email and click "Generate Response" to get an AI-suggested reply.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminResponseTab;
